import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  // Function to save Google avatar as agent avatar
  const saveGoogleAvatarAsAgentAvatar = useCallback(async (googlePictureUrl, idToken) => {
    try {
      console.log('Auto-saving Google avatar as agent avatar:', googlePictureUrl);
      
      // Check if agent already has an avatar
      const agentResponse = await fetch('/api/agents/settings', {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (agentResponse.ok) {
        const agentData = await agentResponse.json();
        // Only save if no agent avatar exists
        if (!agentData.agent_avatar || agentData.agent_avatar.includes('googleusercontent.com')) {
          // Convert Google image to data URL
          const response = await fetch(googlePictureUrl);
          const blob = await response.blob();
          
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
              const dataURL = e.target.result;
              
              try {
                const saveResponse = await fetch(`/api/agents/settings`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    ...agentData,
                    agent_avatar: dataURL
                  })
                });
                
                if (saveResponse.ok) {
                  console.log('Google avatar auto-saved as agent avatar');
                  resolve();
                } else {
                  console.log('Failed to auto-save Google avatar');
                  resolve(); // Don't reject, just log and continue
                }
              } catch (error) {
                console.log('Error auto-saving Google avatar:', error);
                resolve(); // Don't reject, just log and continue
              }
            };
            reader.onerror = () => resolve(); // Don't reject, just continue
            reader.readAsDataURL(blob);
          });
        } else {
          console.log('Agent already has a custom avatar, skipping auto-save');
        }
      }
    } catch (error) {
      console.log('Error checking agent settings for auto-save:', error);
    }
  }, []);

  // Sync Firebase user to database
  const syncUserToDatabase = useCallback(async (firebaseUser) => {
    console.log('🔍 [SYNC_USER] syncUserToDatabase called');
    console.log('🔍 [SYNC_USER] Firebase user:', firebaseUser ? {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified
    } : 'null');
    
    if (!firebaseUser) {
      console.log('🔍 [SYNC_USER] No Firebase user, clearing state');
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 [SYNC_USER] Starting user sync for Firebase user:', firebaseUser.uid);
      
      // Get Firebase ID token
      console.log('🔍 [SYNC_USER] Getting Firebase ID token...');
      const idToken = await firebaseUser.getIdToken();
      console.log('✅ [SYNC_USER] Got Firebase ID token, length:', idToken.length);
      
      // Sync user to database
      const syncUrl = '/auth/sync-user';
      console.log('📡 Calling /auth/sync-user endpoint...', {
        url: syncUrl,
        environment: process.env.NODE_ENV,
        windowLocation: window.location.origin,
        fullUrl: window.location.origin + syncUrl
      });
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000);
      });
      
      let response;
      try {
        // Race between fetch and timeout
        response = await Promise.race([
          fetch(syncUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ idToken })
          }),
          timeoutPromise
        ]);
      } catch (fetchError) {
        console.error('❌ Network error calling /auth/sync-user:', fetchError);
        console.error('Fetch error details:', {
          message: fetchError.message,
          name: fetchError.name,
          stack: fetchError.stack
        });
        throw new Error(`Network error: ${fetchError.message}`);
      }
      
      // Log response status for debugging
      console.log('📥 [SYNC_USER] /auth/sync-user response status:', response.status, response.statusText);
      console.log('📥 [SYNC_USER] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        let data;
        try {
          console.log('🔍 [SYNC_USER] Parsing JSON response...');
          data = await response.json();
          console.log('✅ [SYNC_USER] JSON parsed successfully');
        } catch (jsonError) {
          console.error('❌ [SYNC_USER] Failed to parse JSON response:', jsonError);
          const textResponse = await response.text();
          console.error('❌ [SYNC_USER] Response text:', textResponse);
          throw new Error('Invalid JSON response from server');
        }
        
        console.log('=== [SYNC_USER] AUTH CONTEXT: User synced to database ===');
        console.log('🔍 [SYNC_USER] User data:', data.user);
        console.log('🔍 [SYNC_USER] Agent data:', data.agent);
        
        if (data.user && data.user.id && data.user.email) {
          console.log('✅ AuthContext: Valid user data, setting user');
          setUser(data.user);
          
          // Auto-save Google avatar on first login or if no agent avatar exists
          if (data.user?.picture) {
            await saveGoogleAvatarAsAgentAvatar(data.user.picture, idToken);
          }
        } else {
          console.log('❌ Invalid user data received:', data);
          setUser(null);
        }
      } else {
        let errorData;
        try {
          // Try to parse as JSON first
          const text = await response.text();
          try {
            errorData = JSON.parse(text);
          } catch {
            errorData = { error: 'Unknown error', details: text };
          }
        } catch (textError) {
          errorData = { error: 'Failed to read error response', details: textError.message };
        }
        
        console.error('❌ Failed to sync user to database. Status:', response.status);
        console.error('Error response:', errorData);
        console.error('Response URL:', response.url);
        console.error('Response type:', response.type);
        
        // Create a more descriptive error message
        const errorMessage = errorData.error || 'Unknown error';
        const errorDetails = errorData.details || errorData.message || 'No details available';
        const fullError = new Error(`${errorMessage}: ${errorDetails}`);
        fullError.status = response.status;
        fullError.details = errorData;
        
        setUser(null);
        throw fullError;
      }
    } catch (error) {
      console.error('❌ Error syncing user to database:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setUser(null);
      // Re-throw to allow calling code to handle it
      throw error;
    } finally {
      setLoading(false);
    }
  }, [saveGoogleAvatarAsAgentAvatar]);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔍 [AUTH_CONTEXT] Firebase auth state changed:', firebaseUser ? 'signed in' : 'signed out');
      if (firebaseUser) {
        console.log('🔍 [AUTH_CONTEXT] Firebase user details:', {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          providerData: firebaseUser.providerData.map(p => p.providerId)
        });
      }
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        // Note: We no longer block sync if email isn't verified
        // Users can use the app before verifying email
        // Email verification restrictions will be implemented at the feature level later
        
        const isEmailProvider = firebaseUser.providerData.some(
          provider => provider.providerId === 'password'
        );
        console.log('🔍 [AUTH_CONTEXT] Is email provider:', isEmailProvider);
        console.log('🔍 [AUTH_CONTEXT] Email verified:', firebaseUser.emailVerified);
        
        if (isEmailProvider && !firebaseUser.emailVerified) {
          console.log('⚠️ [AUTH_CONTEXT] User email not verified, but allowing sync (restrictions to be added later)');
        }
        
        console.log('🔍 [AUTH_CONTEXT] Proceeding to sync user to database...');
        try {
          await syncUserToDatabase(firebaseUser);
        } catch (error) {
          // Error is already logged in syncUserToDatabase
          // But we need to make sure loading state is cleared
          console.error('❌ [AUTH_CONTEXT] Failed to sync user after Firebase auth:', error);
          console.error('❌ [AUTH_CONTEXT] Error details:', {
            message: error.message,
            stack: error.stack
          });
          
          // Check if error is due to email not being verified
          if (error.message && error.message.includes('Email not verified')) {
            // User is signed in but email not verified
            // Sign them out and show error message
            console.log('⚠️ [AUTH_CONTEXT] Email not verified, signing out user');
            await firebaseSignOut(auth);
            
            // Store error message for display in UI
            // This will be shown when user tries to sign in again
            sessionStorage.setItem('emailVerificationError', 'Your email address has not been verified. Please check your inbox for the verification email.');
          }
          
          setLoading(false);
          // Don't set user to null here - let the user see the error
          // The syncUserToDatabase already handles setting user to null on error
        }
      } else {
        console.log('🔍 [AUTH_CONTEXT] No Firebase user, clearing user state');
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [syncUserToDatabase]);

  const login = () => {
    // Login is handled by Firebase Auth UI components
    // This function is kept for backward compatibility but doesn't do anything
    console.log('Login should be handled by Firebase Auth UI components');
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      // Clear all possible cached data
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      setFirebaseUser(null);
      
      // Check if we're on a /build route - if so, redirect to home instead of reloading
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/build')) {
        window.location.href = '/';
      } else {
        // Force a page reload to clear any other cached state
        window.location.reload();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Get current Firebase ID token for API calls
  const getToken = async () => {
    if (firebaseUser) {
      return await firebaseUser.getIdToken();
    }
    return null;
  };

  const value = {
    user,
    token: null, // Deprecated - use getToken() instead
    getToken,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    firebaseUser // Expose Firebase user for components that need it
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
